import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from "../config/api";
import "./Account.css";

export default function MyOrders() {
  const { user, isLoggedIn, authLoading } = useAuth();

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");

  useEffect(() => {
    document.title = "My Orders | MayaChitro";
  }, []);

  useEffect(() => {
    let ignore = false;

    async function fetchOrders() {
      if (!isLoggedIn) {
        setOrdersLoading(false);
        return;
      }

      try {
        setOrdersLoading(true);
        setOrdersError("");

        const response = await fetch(buildApiUrl("/orders/my-orders"), {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });

        const data = await response.json();

        if (!ignore && response.ok && data.success) {
          setOrders(data.orders || []);
        } else if (!ignore) {
          setOrders([]);
          setOrdersError(
            data.message ||
              "Order history backend route is not connected yet. This page is ready for integration."
          );
        }
      } catch (error) {
        console.error("My orders fetch error:", error);

        if (!ignore) {
          setOrders([]);
          setOrdersError(
            "Order history backend route is not connected yet. After backend setup, your purchases will appear here."
          );
        }
      } finally {
        if (!ignore) {
          setOrdersLoading(false);
        }
      }
    }

    fetchOrders();

    return () => {
      ignore = true;
    };
  }, [isLoggedIn]);

  if (authLoading) {
    return (
      <main className="account-page">
        <div className="account-loading-card">
          <p className="account-eyebrow">MayaChitro Orders</p>
          <h1>Checking your account...</h1>
          <p>Please wait a moment.</p>
        </div>
      </main>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login?redirect=/my-orders" replace />;
  }

  return (
    <main className="account-page">
      <section className="account-hero">
        <div>
          <p className="account-eyebrow">Order History</p>
          <h1>My Orders</h1>
          <p>
            View your MayaChitro photo purchases, wall art orders, license
            selections, payment status, and download access.
          </p>
        </div>

        <Link to="/shop" className="account-logout-btn">
          Continue Shopping
        </Link>
      </section>

      <section className="account-dashboard-grid">
        <Link to="/account" className="account-stat-card">
          <span>Account</span>
          <strong>{user?.fullName || "Customer"}</strong>
          <small>Manage profile and billing email.</small>
        </Link>

        <Link to="/wishlist" className="account-stat-card">
          <span>Wishlist</span>
          <strong>Saved Items</strong>
          <small>View your favorite products.</small>
        </Link>

        <Link to="/my-downloads" className="account-stat-card">
          <span>Downloads</span>
          <strong>Access Files</strong>
          <small>Open purchased digital downloads.</small>
        </Link>

        <Link to="/collections" className="account-stat-card highlighted">
          <span>Explore</span>
          <strong>Collections</strong>
          <small>Browse photos and wall art by mood.</small>
        </Link>
      </section>

      <section className="account-panel" style={{ maxWidth: "1540px", margin: "24px auto 0" }}>
        <div className="account-panel-heading">
          <p className="account-eyebrow">Purchases</p>
          <h2>Your order history</h2>
          <p>
            Orders will appear here after the user account is connected with
            checkout orders and download tokens.
          </p>
        </div>

        {ordersError && <div className="account-alert error">{ordersError}</div>}

        {ordersLoading ? (
          <div className="account-alert success">Loading orders...</div>
        ) : orders.length > 0 ? (
          <div style={{ display: "grid", gap: "14px", marginTop: "22px" }}>
            {orders.map((order) => (
              <article
                key={order.id || order.orderId || order.order_reference}
                className="account-side-box"
              >
                <p className="account-eyebrow">
                  Order #{order.order_reference || order.id || order.orderId}
                </p>

                <h3>{order.status || "Order"}</h3>

                <ul className="account-status-list">
                  <li>
                    <span>Date</span>
                    <strong>{order.created_at || order.createdAt || "N/A"}</strong>
                  </li>
                  <li>
                    <span>Total</span>
                    <strong>
                      {order.total_amount || order.totalAmount
                        ? `$${Number(order.total_amount || order.totalAmount).toFixed(2)}`
                        : "N/A"}
                    </strong>
                  </li>
                  <li>
                    <span>Email</span>
                    <strong>{order.buyer_email || order.email || user?.email}</strong>
                  </li>
                </ul>

                <Link to="/my-downloads" className="account-contact-link">
                  View Downloads →
                </Link>
              </article>
            ))}
          </div>
        ) : (
          <div className="account-loading-card" style={{ marginTop: "22px" }}>
            <p className="account-eyebrow">No Orders Yet</p>
            <h1>Start your first MayaChitro purchase.</h1>
            <p>
              Your photo downloads, wall art purchases, and license orders will
              appear here after checkout.
            </p>

            <div className="brand-actions" style={{ marginTop: "22px" }}>
              <Link to="/shop?brand=mayachitro" className="btn btn-gold">
                Shop Photography
              </Link>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
